import { useEffect, useState } from 'react'
import { Feedback } from './components/Feedback'
import { CurriculumLibrary } from './components/CurriculumLibrary'
import { Header, type AppScreen } from './components/Header'
import { Home } from './components/Home'
import { Progress } from './components/Progress'
import { Session } from './components/Session'
import { Settings } from './components/Settings'
import type { ExerciseKind, Preferences, ProgressState, RoundResult, TrainingMode } from './domain/types'
import { addRound, loadProgress, resetProgress, saveProgress } from './lib/storage'

type ActiveSession = { kind: ExerciseKind; mode: TrainingMode }

export function App() {
  const [screen, setScreen] = useState<AppScreen>('home')
  const [session, setSession] = useState<ActiveSession | null>(null)
  const [sessionView, setSessionView] = useState<'lesson' | 'settings'>('lesson')
  const [progress, setProgress] = useState<ProgressState>(loadProgress)

  useEffect(() => {
    document.documentElement.dataset.theme = progress.preferences.theme
    document.documentElement.dataset.textSize = progress.preferences.textSize
    const color = progress.preferences.theme === 'paper' ? '#f5f0e8' : progress.preferences.theme === 'contrast' ? '#050505' : '#0a1020'
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color)
    saveProgress(progress)
  }, [progress])

  const navigate = (next: AppScreen) => {
    if (session && next === 'settings') {
      setSessionView('settings')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setSession(null)
    setSessionView('lesson')
    setScreen(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const start = (kind: ExerciseKind, mode: TrainingMode) => {
    setSession({ kind, mode })
    setSessionView('lesson')
    window.scrollTo({ top: 0 })
  }

  const updatePreferences = (preferences: Preferences) => {
    setProgress((current) => ({ ...current, preferences }))
  }

  const completeRound = (result: RoundResult) => {
    setProgress((current) => addRound(current, result))
  }

  const returnToLesson = () => {
    setSessionView('lesson')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleReset = () => {
    const confirmed = window.confirm('Remove all TonalTutor progress and preferences saved in this browser?')
    if (confirmed) setProgress(resetProgress())
  }

  return (
    <div className="app-shell">
      <Header active={sessionView === 'settings' ? 'settings' : session ? 'session' : screen} onNavigate={navigate} />
      {session && sessionView === 'settings' && (
        <div className="lesson-resume-bar">
          <button className="button button-primary" type="button" onClick={returnToLesson}>
            Return to lesson in progress
          </button>
        </div>
      )}
      {session ? (
        <>
          <div hidden={sessionView === 'settings'}>
            <Session
              key={`${session.kind}-${session.mode}`}
              kind={session.kind}
              mode={session.mode}
              preferences={progress.preferences}
              paused={sessionView === 'settings'}
              onComplete={completeRound}
              onExit={() => navigate('home')}
            />
          </div>
          {sessionView === 'settings' && (
            <Settings
              preferences={progress.preferences}
              onChange={updatePreferences}
              inLesson
            />
          )}
        </>
      ) : (
        <>
          {screen === 'home' && <Home progress={progress} onStart={start} onOpenProgress={() => navigate('progress')} onChangePreferences={updatePreferences} />}
          {screen === 'library' && <CurriculumLibrary preferences={progress.preferences} onChange={updatePreferences} />}
          {screen === 'progress' && <Progress progress={progress} onReset={handleReset} />}
          {screen === 'settings' && <Settings preferences={progress.preferences} onChange={updatePreferences} />}
          {screen === 'feedback' && <Feedback />}
        </>
      )}
      {!session && (
        <footer className="site-footer">
          <strong>TonalTutor</strong>
          <span>Ear-training exercises for pitch and harmony.</span>
          <span>Progress is stored on this device.</span>
        </footer>
      )}
    </div>
  )
}
